/**
 * Apps Page — 전체 앱
 *
 * 테넌트 내에서 공유된 챗봇 산출물(HTML 문서·웹앱)을 탐색·실행하는 페이지
 */

import { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Topbar } from '../components/layout';
import { SearchInput } from '../components/ui';
import './AppsPage.css';

// ── 타입 ──────────────────────────────────────────────────────────────────────

type ThumbType = 'chart' | 'doc' | 'form';

interface AppItem {
  id: string;
  name: string;
  desc: string;
  kind: string;        // "HTML 대시보드" | "웹앱" | "HTML 문서"
  scope: string;       // "에티버스 테넌트" | "고객사 2곳" | "지정 사용자"
  cat: string;
  owner: string;
  bot: string;
  runs: number;
  rating: string;
  thumb: ThumbType;
  tint: string;        // 썸네일 배경색
}

// ── 카테고리 목록 ─────────────────────────────────────────────────────────────

const CATS = [
  { id: 'favorite', label: '⭐ 즐겨찾기' },
  { id: 'all',     label: '전체' },
  { id: 'data',    label: '데이터·리포트' },
  { id: 'cloud',   label: '클라우드' },
  { id: 'sales',   label: '영업지원' },
  { id: 'support', label: '기술지원' },
  { id: 'prod',    label: '업무생산성' },
  { id: 'dev',     label: '개발' },
];

// ── 목업 데이터 ────────────────────────────────────────────────────────────────

const APPS: AppItem[] = [
  {
    id: 'a1', name: 'AWS 월간 비용 리포트',
    desc: '워킹온 DB의 고객사별 AWS 청구 데이터를 월 단위 차트와 표로 정리합니다.',
    kind: 'HTML 대시보드', cat: 'data', owner: '김수연', bot: '워킹온 DB 커넥터',
    runs: 342, rating: '4.8', scope: '에티버스 테넌트', thumb: 'chart', tint: '#EEECFC',
  },
  {
    id: 'a2', name: 'EC2 Right-sizing 계산기',
    desc: '인스턴스 사용률을 입력하면 권장 타입과 월 절감 예상액을 계산합니다.',
    kind: '웹앱', cat: 'cloud', owner: '유혜경', bot: '웹검색',
    runs: 157, rating: '4.9', scope: '에티버스 테넌트', thumb: 'chart', tint: '#E6F4EC',
  },
  {
    id: 'a3', name: '회의록 액션아이템 정리기',
    desc: '회의록을 붙여넣으면 담당자·기한별 할 일 표를 만들어 Slack으로 보냅니다.',
    kind: '웹앱', cat: 'prod', owner: '박지훈', bot: '기본 채팅',
    runs: 211, rating: '4.7', scope: '에티버스이피에이', thumb: 'form', tint: '#E6F4EC',
  },
  {
    id: 'a4', name: 'Support-on 장애 접수 폼',
    desc: '증상·영향 범위·첨부를 받아 Support-on 가이드 기준으로 1차 분류 결과를 보여줍니다.',
    kind: '웹앱', cat: 'support', owner: '엄재한', bot: 'Support-on 가이드',
    runs: 189, rating: '4.6', scope: '에티버스 테넌트', thumb: 'form', tint: '#E8F0FC',
  },
  {
    id: 'a5', name: 'MSP 제안서 표지 생성기',
    desc: '고객사명과 사업명을 넣으면 회사 양식의 제안서 표지를 만들어 PDF로 저장합니다.',
    kind: '웹앱', cat: 'sales', owner: '엄재한', bot: '기본 채팅',
    runs: 128, rating: '4.6', scope: '고객사 2곳', thumb: 'doc', tint: '#E8F0FC',
  },
  {
    id: 'a6', name: '고객사 미팅 사전 브리프',
    desc: '워킹온 CRM의 딜·이력 정보를 한 장짜리 미팅 브리프로 정리합니다.',
    kind: 'HTML 문서', cat: 'sales', owner: '김수연', bot: '워킹온 DB 커넥터',
    runs: 103, rating: '4.4', scope: '지정 사용자', thumb: 'doc', tint: '#FDF1DC',
  },
  {
    id: 'a7', name: 'AI-DLC 요구사항 체크리스트',
    desc: 'AI-DLC 문서 가이드 기준으로 단계별 산출물 누락 여부를 점검합니다.',
    kind: 'HTML 문서', cat: 'dev', owner: '엄재한', bot: 'AI DLC 문서 가이드',
    runs: 96, rating: '4.5', scope: '이테크 시스템', thumb: 'doc', tint: '#FDF1DC',
  },
  {
    id: 'a8', name: '주간 IT 뉴스 브리핑 카드',
    desc: '웹크롤링으로 모은 네이버 뉴스를 주제별 카드로 요약해 보여줍니다.',
    kind: 'HTML 문서', cat: 'data', owner: '이서윤', bot: '웹크롤링 워크플로우',
    runs: 74, rating: '4.3', scope: '에티버스 테넌트', thumb: 'chart', tint: '#F3EEFB',
  },
];

const SORT_OPTIONS = [
  { value: 'runs',   label: '많이 실행된 순' },
  { value: 'recent', label: '최근 게시 순' },
  { value: 'rating', label: '평점 높은 순' },
];

// ── 썸네일 렌더 ────────────────────────────────────────────────────────────────

function Thumbnail({ type }: { type: ThumbType }) {
  if (type === 'chart') {
    return (
      <div className="app-thumb app-thumb--chart">
        <div className="app-thumb-header">
          <span className="app-thumb-bar app-thumb-bar--title" />
          <span className="app-thumb-bar app-thumb-bar--sub" />
        </div>
        <div className="app-thumb-chart-bars">
          <span style={{ height: 30 }} />
          <span style={{ height: 48 }} />
          <span style={{ height: 38 }} />
          <span style={{ height: 58 }} />
          <span style={{ height: 44 }} />
        </div>
      </div>
    );
  }
  if (type === 'doc') {
    return (
      <div className="app-thumb app-thumb--doc">
        <span className="app-thumb-doc-title" />
        <span className="app-thumb-line" style={{ width: '100%' }} />
        <span className="app-thumb-line" style={{ width: '92%' }} />
        <span className="app-thumb-line" style={{ width: '84%' }} />
        <span className="app-thumb-line" style={{ width: '56%' }} />
      </div>
    );
  }
  return (
    <div className="app-thumb app-thumb--form">
      <span className="app-thumb-form-label" />
      <span className="app-thumb-form-input" />
      <span className="app-thumb-form-label" />
      <span className="app-thumb-form-input" />
      <span className="app-thumb-form-btn" />
    </div>
  );
}

// ── 컴포넌트 ──────────────────────────────────────────────────────────────────

export function AppsPage() {
  const navigate = useNavigate();
  const [cat,     setCat]     = useState('all');
  const [sortBy,  setSortBy]  = useState('runs');
  const [search,  setSearch]  = useState('');
  const [favorites, setFavorites] = useState<string[]>(() => {
    try { return JSON.parse(localStorage.getItem('library_favorites') || '[]'); } catch { return []; }
  });

  const toggleFavorite = (id: string, e: React.MouseEvent) => {
    e.stopPropagation();
    const next = favorites.includes(id) ? favorites.filter(f => f !== id) : [...favorites, id];
    setFavorites(next);
    localStorage.setItem('library_favorites', JSON.stringify(next));
  };

  const filtered = useMemo(() => {
    let list = APPS.filter((a) => {
      if (cat === 'favorite') return favorites.includes(a.id);
      if (cat !== 'all' && a.cat !== cat) return false;
      if (search) {
        const q = search.toLowerCase();
        return a.name.toLowerCase().includes(q) || a.owner.toLowerCase().includes(q) || a.bot.toLowerCase().includes(q);
      }
      return true;
    });
    if (sortBy === 'runs')   list = [...list].sort((a, b) => b.runs  - a.runs);
    if (sortBy === 'rating') list = [...list].sort((a, b) => parseFloat(b.rating) - parseFloat(a.rating));
    return list;
  }, [cat, sortBy, search, favorites]);

  const catCounts = useMemo(() => {
    const m: Record<string, number> = { all: APPS.length, favorite: favorites.length };
    for (const c of CATS.slice(2)) m[c.id] = APPS.filter((a) => a.cat === c.id).length;
    return m;
  }, [favorites]);

  const handleOpen = (app: AppItem) => {
    navigate(`/library/${app.id}`);
  };

  return (
    <>
      <Topbar
        title="라이브러리"
        subtitle="테넌트 안에서 공유된 챗봇 산출물(HTML 문서·웹앱)을 찾아 바로 실행하세요."
        actions={
          <div className="apps-header-actions">
            <SearchInput
              placeholder="항목 이름, 만든 사람, 챗봇으로 검색"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
        }
      />

      <div className="page-content">
        <div className="page-content-inner apps-container">

          {/* ── 공유 흐름 안내 ── */}
          <section className="apps-flow-banner">
            {[
              { n: 1, title: '챗봇에서 산출물 생성',  desc: '대화 중 만든 HTML·웹앱을 그대로 가져옵니다' },
              { n: 2, title: '공유 신청',               desc: '이름·설명·공개 범위·데이터 권한을 정합니다' },
              { n: 3, title: '사전 점검·승인',          desc: '자동 보안 점검 후 관리자가 승인합니다' },
              { n: 4, title: '라이브러리 게시·실행',  desc: '권한 있는 사용자가 샌드박스에서 실행합니다', active: true },
            ].map((step) => (
              <div key={step.n} className="apps-flow-step">
                <span className={`apps-flow-num${step.active ? ' apps-flow-num--active' : ''}`}>{step.n}</span>
                <div>
                  <strong>{step.title}</strong>
                  <span>{step.desc}</span>
                </div>
              </div>
            ))}
          </section>

          {/* ── 상태 알림 배너 ── */}
          <div className="apps-status-banner" role="status">
            <span>내가 공유 신청한 항목 중 <strong>1건이 검토 중</strong>이고, <strong>1건은 수정 요청</strong>을 받았어요.</span>
            <button type="button" className="apps-status-link" onClick={() => navigate('/library/my')}>내가 공유한 항목에서 확인 →</button>
          </div>

          {/* ── 카테고리 + 정렬 ── */}
          <div className="apps-toolbar">
            <div className="apps-chips" role="group" aria-label="카테고리">
              {CATS.map((c) => (
                <button
                  key={c.id}
                  type="button"
                  aria-pressed={cat === c.id}
                  className={`apps-chip${cat === c.id ? ' apps-chip--active' : ''}`}
                  onClick={() => setCat(c.id)}
                >
                  {c.label}
                  <span className="apps-chip-count">{catCounts[c.id] ?? 0}</span>
                </button>
              ))}
            </div>
            <div className="apps-toolbar-right">
              <span className="apps-count">{filtered.length}개 항목</span>
              <label className="apps-sort-label">
                정렬
                <select
                  className="apps-sort-select"
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                >
                  {SORT_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
              </label>
            </div>
          </div>

          {/* ── 앱 그리드 ── */}
          {filtered.length > 0 ? (
            <div className="apps-grid">
              {filtered.map((app) => (
                <article key={app.id} className="app-card">
                  {/* 썸네일 */}
                  <div className="app-card-thumb" style={{ background: app.tint }}>
                    <Thumbnail type={app.thumb} />
                    {/* 즐겨찾기 버튼 */}
                    <button
                      type="button"
                      className={`app-card-fav-btn${favorites.includes(app.id) ? ' app-card-fav-btn--on' : ''}`}
                      onClick={(e) => toggleFavorite(app.id, e)}
                      title={favorites.includes(app.id) ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                      aria-label={favorites.includes(app.id) ? '즐겨찾기 해제' : '즐겨찾기 추가'}
                    >
                      ★
                    </button>
                  </div>
                  {/* 본문 */}
                  <div className="app-card-body">
                    <div className="app-card-tags">
                      <span className="app-tag-kind">{app.kind}</span>
                      <span className="app-tag-scope">{app.scope}</span>
                    </div>
                    <h2 className="app-card-name">{app.name}</h2>
                    <p className="app-card-desc">{app.desc}</p>
                    <div className="app-card-by">
                      <span className="app-card-avatar">{app.owner.charAt(0)}</span>
                      <span className="app-card-owner-text">
                        {app.owner}
                        <span className="app-card-owner-sub"> · {app.bot} 챗봇</span>
                      </span>
                    </div>
                    <div className="app-card-footer">
                      <div className="app-card-stats">
                        <span>
                          <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M7 5l12 7-12 7z" /></svg>
                          {app.runs.toLocaleString()}회
                        </span>
                        <span>
                          <svg aria-hidden="true" width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="#B7791F" strokeWidth="2"><path d="M12 3l2.7 5.6 6.1.9-4.4 4.3 1 6.1L12 17l-5.4 2.9 1-6.1-4.4-4.3 6.1-.9z" /></svg>
                          {app.rating}
                        </span>
                      </div>
                      <button type="button" className="app-card-open-btn" onClick={() => handleOpen(app)}>
                        열기
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          ) : (
            <div className="apps-empty">
              <p>검색 결과가 없습니다.</p>
              <p>다른 키워드나 카테고리로 검색해보세요.</p>
            </div>
          )}
        </div>
      </div>
    </>
  );
}

export default AppsPage;
